import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import axios from "axios";
import type { RootState } from "src/common/state/store";
import type { RagAgent, RagConversation, RagConversationSummary } from "../api/ragApi";
import {
  fetchAgents,
  createAgent,
  updateAgent,
  deleteAgent,
  fetchConversations,
  fetchConversation,
  deleteConversation,
} from "../api/ragApi";

type CreateAgentData = Parameters<typeof createAgent>[0];
type UpdateAgentData = Parameters<typeof updateAgent>[1];

type RagAgentsStatus = "idle" | "loading" | "loaded" | "error";

const RAG_CONVERSATIONS_PAGE_SIZE = 20;

interface IRagConversationsPagination {
  hasMore: boolean;
  isLoadingMore: boolean;
}

interface IRagState {
  agents: RagAgent[];
  status: RagAgentsStatus;
  error: string | null;
  /** Sidebar rows (summaries — no message bodies). */
  conversations: RagConversationSummary[];
  conversationsPagination: IRagConversationsPagination;
  conversationsStatus: RagAgentsStatus;
  /** The thread the chat pane is currently showing; null = unsaved new thread. */
  activeConversationId: number | null;
  /** Full thread most recently opened from the sidebar, for the hook to hydrate from. */
  activeConversation: RagConversation | null;
}

const initialState: IRagState = {
  agents: [],
  status: "idle",
  error: null,
  conversations: [],
  conversationsPagination: {
    hasMore: true,
    isLoadingMore: false,
  },
  conversationsStatus: "idle",
  activeConversationId: null,
  activeConversation: null,
};

export const fetchAgentsThunk = createAsyncThunk<RagAgent[], void, { state: RootState }>(
  "rag/fetchAgents",
  async () => fetchAgents(),
  {
    condition: (_, { getState }) => {
      const status = getState().rag.status;
      return status !== "loading" && status !== "loaded";
    },
  },
);

export const refetchAgentsThunk = createAsyncThunk<RagAgent[], void, { state: RootState }>(
  "rag/refetchAgents",
  async () => fetchAgents(),
);

const extractErrorMessage = (err: unknown): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { error?: { message?: string } } | undefined;
    return data?.error?.message || "";
  }
  return "";
};

export const createAgentThunk = createAsyncThunk<
  RagAgent,
  CreateAgentData,
  { state: RootState; rejectValue: string }
>("rag/createAgent", async (data, { dispatch, rejectWithValue }) => {
  try {
    const agent = await createAgent(data);
    await dispatch(refetchAgentsThunk());
    return agent;
  } catch (err) {
    return rejectWithValue(extractErrorMessage(err));
  }
});

export const updateAgentThunk = createAsyncThunk<
  RagAgent,
  { id: number; data: UpdateAgentData },
  { state: RootState; rejectValue: string }
>("rag/updateAgent", async ({ id, data }, { dispatch, rejectWithValue }) => {
  try {
    const agent = await updateAgent(id, data);
    await dispatch(refetchAgentsThunk());
    return agent;
  } catch (err) {
    return rejectWithValue(extractErrorMessage(err));
  }
});

export const deleteAgentThunk = createAsyncThunk<number, number, { state: RootState }>(
  "rag/deleteAgent",
  async (id, { dispatch }) => {
    await deleteAgent(id);
    await dispatch(refetchAgentsThunk());
    return id;
  },
);

export const fetchConversationsThunk = createAsyncThunk<
  { data: RagConversationSummary[]; total: number },
  void,
  { state: RootState }
>("rag/fetchConversations", async () => fetchConversations(0, RAG_CONVERSATIONS_PAGE_SIZE));

export const fetchMoreConversationsThunk = createAsyncThunk<
  { data: RagConversationSummary[]; total: number },
  { start: number },
  { state: RootState }
>("rag/fetchMoreConversations", async ({ start }) =>
  fetchConversations(start, RAG_CONVERSATIONS_PAGE_SIZE),
);

export const openConversationThunk = createAsyncThunk<
  RagConversation,
  number,
  { state: RootState }
>("rag/openConversation", async (conversationId) => fetchConversation(conversationId));

export const deleteConversationThunk = createAsyncThunk<number, number, { state: RootState }>(
  "rag/deleteConversation",
  async (conversationId) => {
    await deleteConversation(conversationId);
    return conversationId;
  },
);

export const ragSlice = createSlice({
  name: "rag",
  initialState,
  reducers: {
    invalidateAgents: (state) => {
      state.status = "idle";
    },
    /** "Nouvelle requête": drop the active thread so the next ask opens a new one. */
    startNewConversation: (state) => {
      state.activeConversationId = null;
      state.activeConversation = null;
    },
    /**
     * Adopts the id the backend minted for a thread that started unsaved. Only
     * the id changes — `activeConversation` stays null, because the live messages
     * already sit in the chat hook and re-hydrating from the server here would
     * wipe the streaming turn out from under the user.
     */
    setActiveConversationId: (state, { payload }: { payload: number }) => {
      state.activeConversationId = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchConversationsThunk.pending, (state) => {
      state.conversationsStatus = "loading";
    });
    builder.addCase(fetchConversationsThunk.fulfilled, (state, { payload }) => {
      state.conversations = payload.data;
      state.conversationsStatus = "loaded";
      state.conversationsPagination = {
        hasMore: payload.data.length < payload.total,
        isLoadingMore: false,
      };
    });
    builder.addCase(fetchConversationsThunk.rejected, (state) => {
      state.conversationsStatus = "error";
      state.conversationsPagination.isLoadingMore = false;
    });

    builder.addCase(fetchMoreConversationsThunk.pending, (state) => {
      state.conversationsPagination.isLoadingMore = true;
    });
    builder.addCase(fetchMoreConversationsThunk.fulfilled, (state, { payload }) => {
      const known = new Set(state.conversations.map((c) => c.id));
      state.conversations.push(...payload.data.filter((c) => !known.has(c.id)));
      state.conversationsPagination = {
        hasMore: state.conversations.length < payload.total,
        isLoadingMore: false,
      };
    });
    builder.addCase(fetchMoreConversationsThunk.rejected, (state) => {
      state.conversationsPagination.isLoadingMore = false;
    });

    builder.addCase(openConversationThunk.fulfilled, (state, { payload }) => {
      state.activeConversation = payload;
      state.activeConversationId = payload.id;
    });

    builder.addCase(deleteConversationThunk.fulfilled, (state, { payload }) => {
      state.conversations = state.conversations.filter((c) => c.id !== payload);
      if (state.activeConversationId === payload) {
        state.activeConversationId = null;
        state.activeConversation = null;
      }
    });

    builder
      .addMatcher(isAnyOf(fetchAgentsThunk.pending, refetchAgentsThunk.pending), (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addMatcher(
        isAnyOf(fetchAgentsThunk.fulfilled, refetchAgentsThunk.fulfilled),
        (state, { payload }) => {
          state.agents = payload;
          state.status = "loaded";
        },
      )
      .addMatcher(
        isAnyOf(fetchAgentsThunk.rejected, refetchAgentsThunk.rejected),
        (state, { error }) => {
          state.status = "error";
          state.error = error.message ?? "Failed to fetch RAG agents";
        },
      );
  },
});

export const ragReducer = ragSlice.reducer;
export const { invalidateAgents, startNewConversation, setActiveConversationId } = ragSlice.actions;

export const getRagAgents = (state: RootState) => state.rag.agents;
export const getRagAgentsStatus = (state: RootState) => state.rag.status;

export const getRagConversations = (state: RootState) => state.rag.conversations;
export const getRagConversationsPagination = (state: RootState) =>
  state.rag.conversationsPagination;
export const getRagConversationsStatus = (state: RootState) => state.rag.conversationsStatus;
export const getActiveRagConversationId = (state: RootState) => state.rag.activeConversationId;
export const getActiveRagConversation = (state: RootState) => state.rag.activeConversation;
